import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PhaseSixIcon from "../phaseSixIcon/PhaseSixIcon";

interface UnderlinedInputProps {
    text: string;
    setText: (text: string) => void;
    initialLines?: number;
    maxLines?: number;
    onEnter?: () => void;
    inputRef?: React.Ref<HTMLTextAreaElement>;
    "data-testid"?: string;
}

/**
 * The parent container must give this component its full available height
 * and must not be scrollable.
 *
 * If the parent scrolls, the underlines will scroll out of sync with the textarea content.
 */
const UnderlinedInput = (props: UnderlinedInputProps) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);

    const [lineCount, setLineCount] = useState(1);
    const [isFocused, setIsFocused] = useState(false);

    const initialLines = props.initialLines ?? 1;
    const maxLines = props.maxLines;
    const isSingleLine = maxLines === 1;
    const showEditIcon = !props.text.trim() && !isFocused;

    const recalculateHeight = useCallback(() => {
        const textArea = internalRef.current;
        if (!textArea) return;

        textArea.style.height = "auto";

        const lineHeight = parseInt(getComputedStyle(textArea).lineHeight, 10);
        const lines = Math.floor(textArea.scrollHeight / lineHeight);
        const clampedLines = maxLines ? Math.min(lines, maxLines) : lines;
        setLineCount(clampedLines);

        const scrollHeight = textArea.scrollHeight;
        const maxHeight = maxLines ? lineHeight * maxLines : scrollHeight;
        textArea.style.height = Math.min(scrollHeight, maxHeight) + "px";
    }, [maxLines]);

    // useLayoutEffect: ensures height is recalculated on navigation and refresh (useEffect runs after paint, causing a stale height)
    useLayoutEffect(() => {
        recalculateHeight();
    }, [props.text, recalculateHeight]);

    useEffect(() => {
        window.addEventListener("resize", recalculateHeight);
        return () => window.removeEventListener("resize", recalculateHeight);
    }, [recalculateHeight]);

    // inputRef lets parents access the textarea (e.g. for focus management) without forwardRef, which is deprecated in React 19.
    // mergeRefs ensures both the internal ref (used for layout calculations) and the parent's ref stay in sync.
    const { inputRef } = props;
    const mergeRefs = useCallback(
        (el: HTMLTextAreaElement | null) => {
            (internalRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
            if (typeof inputRef === "function") inputRef(el);
            else if (inputRef) (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
        },
        [inputRef]
    );

    const renderLines = (amount: number) => {
        const lines = [];
        const amountOfLines = Math.max(initialLines, amount);

        for (let i = amountOfLines; i > 0; i--) {
            lines.push(
                <div
                    key={i}
                    className="h-l w-full border-b-2 border-p6-grey-light"
                />
            );
        }

        return <div className="flex flex-col absolute w-full h-full">{lines}</div>;
    };

    return (
        <div className="flex relative w-full">
            {renderLines(lineCount)}
            {showEditIcon && (
                <PhaseSixIcon
                    name="edit-avatar"
                    className="absolute z-10 text-xxl text-p6-grey-mid pointer-events-none left-xs -top-xxs"
                />
            )}
            <textarea
                className={`w-full font-handwriting leading-l text-l z-10 bg-transparent border-none px-s resize-none focus:outline-none ${
                    isSingleLine ? "overflow-hidden whitespace-nowrap" : ""
                }`}
                style={{ minHeight: `calc(var(--spacing-l) * ${initialLines})` }}
                onChange={(e) => props.setText(e.target.value)}
                // Prevent textarea newline on Enter; let parent handle focus navigation via onEnter
                onKeyDown={(e) => {
                    if (e.key === "Enter" && props.onEnter) {
                        e.preventDefault();
                        props.onEnter();
                    }
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                ref={mergeRefs}
                rows={1}
                value={props.text}
                data-testid={props["data-testid"]}
            />
        </div>
    );
};

export default UnderlinedInput;
